import { useState, useEffect } from 'react';
import { Settings, Save, Globe, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ConfiguracionSistema() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [frontendUrl, setFrontendUrl] = useState('');
  const [urlActual, setUrlActual] = useState('');

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/configuracion/sistema`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setFrontendUrl(data.frontend_url);
        setUrlActual(data.frontend_url);
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cargar configuración');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!frontendUrl) {
      toast.error('La URL no puede estar vacía');
      return;
    }

    if (!frontendUrl.startsWith('http://') && !frontendUrl.startsWith('https://')) {
      toast.error('La URL debe comenzar con http:// o https://');
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/configuracion/sistema`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ frontend_url: frontendUrl })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        setUrlActual(frontendUrl);
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Error al guardar configuración');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al guardar configuración');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-yellow-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Cargando configuración...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black p-6">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Configuración del Sistema</h1>
          <p className="text-gray-400">Gestiona las configuraciones globales de la aplicación</p>
        </div>

        {/* Card de URL del Frontend */}
        <Card className="bg-gray-800/50 border-gray-700 mb-6">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Globe className="w-5 h-5 text-blue-500" />
              URL del Frontend
            </CardTitle>
            <CardDescription className="text-gray-400">
              Esta URL se usa para generar todos los links en los emails automáticos
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                URL Principal del Frontend
              </label>
              <Input
                type="text"
                value={frontendUrl}
                onChange={(e) => setFrontendUrl(e.target.value)}
                placeholder="https://retiros.kompa.global"
                className="bg-gray-700/50 border-gray-600 text-white"
              />
              <p className="text-xs text-gray-500 mt-2">
                Ejemplo: https://retiros.kompa.global (sin / al final)
              </p>
            </div>

            {urlActual !== frontendUrl && (
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                <p className="text-yellow-300 text-sm">
                  ⚠️ Hay cambios sin guardar. Click en "Guardar Configuración" para aplicarlos.
                </p>
              </div>
            )}

            <div className="flex gap-3">
              <Button
                onClick={handleSave}
                disabled={saving || urlActual === frontendUrl}
                className="bg-green-500 hover:bg-green-600 text-white flex-1"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Guardando...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Guardar Configuración
                  </>
                )}
              </Button>
              
              {urlActual !== frontendUrl && (
                <Button
                  onClick={() => setFrontendUrl(urlActual)}
                  variant="outline"
                  className="border-gray-700 text-white hover:bg-gray-700"
                >
                  Cancelar
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Información Adicional */}
        <Card className="bg-blue-500/10 border-blue-500/30">
          <CardContent className="p-6">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-blue-400 flex-shrink-0 mt-1" />
              <div className="space-y-2">
                <p className="text-blue-300 font-semibold">¿Cómo funciona?</p>
                <ul className="text-blue-200 text-sm space-y-1 list-disc list-inside">
                  <li>Esta URL se usa para generar links en TODOS los emails</li>
                  <li>Los cambios se aplican inmediatamente (sin necesidad de reiniciar)</li>
                  <li>Después de cambiar la URL, regenera los templates de email</li>
                  <li>Los nuevos emails usarán la URL configurada aquí</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* URLs que se Generarán */}
        <Card className="bg-gray-800/50 border-gray-700 mt-6">
          <CardHeader>
            <CardTitle className="text-white text-lg">Vista Previa de Links</CardTitle>
            <CardDescription className="text-gray-400">
              Así se verán los links en los emails con la URL actual
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 font-mono text-sm">
              <div className="bg-gray-900/50 p-3 rounded-lg">
                <span className="text-gray-400">Formulario 1:</span>
                <span className="text-yellow-500 ml-2">{frontendUrl}/formulario-1</span>
              </div>
              <div className="bg-gray-900/50 p-3 rounded-lg">
                <span className="text-gray-400">Agendar Cita:</span>
                <span className="text-yellow-500 ml-2">{frontendUrl}/agendar-cita/[id]</span>
              </div>
              <div className="bg-gray-900/50 p-3 rounded-lg">
                <span className="text-gray-400">Formulario 2:</span>
                <span className="text-yellow-500 ml-2">{frontendUrl}/formulario-2/[id]</span>
              </div>
              <div className="bg-gray-900/50 p-3 rounded-lg">
                <span className="text-gray-400">Firma:</span>
                <span className="text-yellow-500 ml-2">{frontendUrl}/firmar/[id]</span>
              </div>
              
              {/* NUEVO: Link VIP */}
              <div className="bg-gradient-to-r from-purple-900/50 to-yellow-900/50 p-3 rounded-lg border border-yellow-500/30">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <span className="text-yellow-400 font-semibold">🌟 Registro VIP:</span>
                    <span className="text-yellow-500 ml-2">{frontendUrl}/registro-vip</span>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => {
                      navigator.clipboard.writeText(`${frontendUrl}/registro-vip`);
                      toast.success('Link VIP copiado al portapapeles');
                    }}
                    className="bg-yellow-500 hover:bg-yellow-600 text-black text-xs"
                  >
                    📋 Copiar
                  </Button>
                </div>
                <p className="text-xs text-gray-400 mt-2">
                  Link especial para clientes VIP (proceso acelerado sin citas)
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
